<template>
  <div class="competition-standings">
    <NotificationBanner
      v-if="isNil(slip) || isNil(slip.bowlers) || slip.bowlers.length === 0"
      :message="$t('competition-standings.empty-results')"
    />

    <div v-else :class="bodyClassList">
      <div></div>
      <div>
        <small>{{ $t('competition-standings.heading-name') }}</small>
      </div>
      <div>
        <small>{{ $t('competition-standings.heading-club') }}</small>
      </div>
      <div>
        <small>{{ $t('competition-standings.heading-association') }}</small>
      </div>
      <div>
        <small>{{ $t('competition-standings.heading-klass') }}</small>
      </div>

      <template v-if="slip.showBestScoreOnly">
        <div>
          <small>{{ $t('competition-standings.heading-score') }}</small>
        </div>
        <div>
          <small>{{ $t('competition-standings.heading-handicap') }}</small>
        </div>
        <div>
          <small>{{ $t('competition-standings.heading-strike-count') }}</small>
        </div>
        <div>
          <small>{{
            $t('competition-standings.heading-last-game-score')
          }}</small>
        </div>
      </template>

      <template v-else>
        <div>
          <small>{{ $t('competition-standings.heading-score') }}</small>
        </div>
        <div>
          <small>{{ $t('competition-standings.heading-calculation') }}</small>
        </div>
        <div>
          <small>{{
            $t('competition-standings.heading-last-game-score')
          }}</small>
        </div>
      </template>

      <template v-for="(bowler, index) in slip.bowlers" :key="index">
        <div>
          <small v-if="bowler.ruleScore.rank">
            {{ formatFloat(bowler.ruleScore.rank, 0) }}
          </small>
          <small v-else>{{ 1 + index }}</small>
        </div>
        <div>
          <small>{{ formatValue(bowler.fullName) }}</small>
        </div>
        <div>
          <small>{{ formatValue(bowler.membership.clubName) }}</small>
        </div>
        <div>
          <small>{{ formatValue(bowler.membership.associationName) }}</small>
        </div>
        <div>
          <small>{{ formatValue(bowler.klassName) }}</small>
        </div>

        <template v-if="slip.showBestScoreOnly">
          <div>
            <small>{{ formatFloat(bowler.bestScore.total, 0) }}</small>
          </div>
          <div>
            <small>{{ formatFloat(bowler.bestScore.handicap, 0) }}</small>
          </div>
          <div>
            <small>{{ formatFloat(bowler.bestScore.strikeCount, 0) }}</small>
          </div>
          <div>
            <small>{{ formatFloat(bowler.bestScore.lastGame, 0) }}</small>
          </div>
        </template>

        <template v-else>
          <div>
            <small>{{ formatValue(bowler.ruleScore.total) }}</small>
          </div>
          <div>
            <small>{{ formatValue(bowler.ruleScore.calculation) }}</small>
          </div>
          <div>
            <small>{{ formatValue(bowler.ruleScore.lastGameScore) }}</small>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { PropType, computed } from 'vue'

import NotificationBanner from '@/components/common/NotificationBanner.vue'
import { CompetitionIndividualStandingsSlip } from '@/store/modules/competition.types'
import { formatFloat, formatValue } from '@/utils/format'

const props = defineProps({
  slip: {
    type: Object as PropType<CompetitionIndividualStandingsSlip | null>,
    default: null,
  },
})

const bodyClassList = computed<string[]>((): string[] => [
  'competition-standings-body',
  `n-cols-${props.slip?.showBestScoreOnly ? 9 : 8}`,
])
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

.competition-standings {
  overflow-x: auto;

  .competition-standings-body {
    display: grid;

    div {
      padding: 0.25em 0.75em;
      text-align: center;
      white-space: nowrap;
      border-bottom: $border-width solid $color-accent;
    }

    &.n-cols-8 {
      grid-template-columns: repeat(8, auto);

      div:nth-child(-n + 8) {
        background-color: $color-accent;
        color: $color-text-inverted;
        font-weight: bold;
      }

      div:nth-child(8n + 1) {
        text-align: right;
      }

      div:nth-child(8n + 2),
      div:nth-child(8n + 3),
      div:nth-child(8n + 4) {
        text-align: left;
      }
    }

    &.n-cols-9 {
      grid-template-columns: repeat(9, auto);

      div:nth-child(-n + 9) {
        background-color: $color-accent;
        color: $color-text-inverted;
        font-weight: bold;
      }

      div:nth-child(9n + 1) {
        text-align: right;
      }

      div:nth-child(9n + 2),
      div:nth-child(9n + 3),
      div:nth-child(9n + 4) {
        text-align: left;
      }
    }
  }
}
</style>
